import { Radar, ShieldAlert } from "lucide-react"

import type { MissingTalentSummary } from "@/lib/talent-tracking-api"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"

import { SEASONS } from "./constants"

type SeasonBoss = {
  id: string
  name: string
  season: string
  missingCount: number
  talents: MissingTalentSummary[]
}

export function BossFocusSidebar({
  seasonBosses,
  selectedSeason,
  selectedBoss,
  bossSortMode,
  onUpdateSearch,
}: {
  seasonBosses: SeasonBoss[]
  selectedSeason: string
  selectedBoss: SeasonBoss | null
  bossSortMode: string
  onUpdateSearch: (
    updates: Record<string, string | undefined>,
    options?: { replace?: boolean },
  ) => void
}) {
  return (
    <Card size="sm" className="xl:sticky xl:top-4 xl:self-start">
      <CardHeader>
        <CardHeading
          icon={<Radar className="size-4 text-primary" />}
          className="text-lg"
        >
          Boss Focus
        </CardHeading>
        <CardDescription>
          Narrow the raid board to one seasonal boss at a time.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["most-missing", "Most Missing"],
              ["least-missing", "Least Missing"],
              ["name", "Name"],
            ] as const
          ).map(([mode, label]) => (
            <Button
              unstyled
              key={mode}
              type="button"
              onClick={() => onUpdateSearch({ bossSort: mode })}
              className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${bossSortMode === mode ? "border-primary/25 bg-primary/10 text-primary" : "border-border/60 bg-card/65 text-foreground hover:border-primary/15"}`}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {SEASONS.map((season) => (
            <Button
              unstyled
              key={season}
              type="button"
              onClick={() => {
                onUpdateSearch({
                  season,
                  bossId: undefined,
                })
              }}
              className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${season === selectedSeason ? "border-primary/25 bg-primary/10 text-primary" : "border-border/60 bg-card/65 text-foreground hover:border-primary/15"}`}
            >
              {season}
            </Button>
          ))}
        </div>

        <div className="grid gap-2">
          {seasonBosses.map((boss) => (
            <Button
              unstyled
              key={boss.id}
              type="button"
              onClick={() => onUpdateSearch({ bossId: boss.id })}
              className={`inline-card text-left transition ${selectedBoss?.id === boss.id ? "border-primary/30 bg-primary/8" : "bg-card/50 hover:border-primary/15"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="truncate text-sm font-semibold text-foreground">
                    {boss.name}
                  </div>
                  <div className="text-kicker mt-1 text-[0.72rem] font-medium tracking-[0.16em] text-muted-foreground">
                    {boss.missingCount} missing players
                  </div>
                </div>
                <ShieldAlert className="size-4 text-primary" />
              </div>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
